import { useQuery } from 'react-query'
import { useParams } from 'react-router'
import { RouteParameters } from '../../constants'
import { Appointment } from '../../types'
import { logFactory } from '../../util/logFactory'
import { useAppointmentsService } from '../services'
import { createContext, FC, useContext } from 'react'

interface UseAppointmentContext {
  appointment?: Appointment
  isError: boolean
  isLoading: boolean
}

const hookName = 'useAppointment'
const log = logFactory(hookName)

const defaultContext: UseAppointmentContext = {
  appointment: undefined,
  isError: false,
  isLoading: false,
}

const context = createContext<UseAppointmentContext>(defaultContext)

const { Provider } = context

const AppointmentProvider: FC = ({ children }) => {
  const { [RouteParameters.APPOINTMENT_ID]: appointmentId, [RouteParameters.APPOINTMENT_GID]: appointmentGid } = useParams()
  const { getAppointment, isReady } = useAppointmentsService()

  const fetchAppointment = async (): Promise<Appointment | undefined> => {
    log(`🗓️ Fetching appointment...`, appointmentId || appointmentGid)

    const found = await getAppointment({ id: appointmentId, gid: appointmentGid })

    log(`🗓️ Found appointment`, found)

    return found
  }

  const isQueryEnabled = Boolean((appointmentId || appointmentGid) && isReady)

  const {
    data: appointment,
    isError,
    isLoading,
  } = useQuery([hookName, appointmentId || appointmentGid], fetchAppointment, {
    enabled: isQueryEnabled,
  })

  const contextValue: UseAppointmentContext = {
    appointment,
    isError,
    isLoading: (!isReady || isLoading) && !appointment,
  }

  return <Provider value={contextValue}>{children}</Provider>
}

const useAppointment = (): UseAppointmentContext => useContext(context)

export { AppointmentProvider, useAppointment }
